import React, { useEffect, useState } from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import useLiveChat from '~/layout/utils/useLiveChat';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { Script } from '~/techstyle-shared/react-marketing';
import { useDomain } from '~/techstyle-shared/redux-core';

import LiveChatSurvey from '../LiveChatSurvey';

const pureCloudConfig = config.get('public.pureCloud.config');
const pureCloudScript = config.get('public.pureCloud.script');
const CONTAINER_ID = 'chat-container';
const requiredKeys = ['firstName', 'lastName', 'email', 'reasoning'];

const Container = styled.div`
  height: 100vh;
`;

const launchChatWidget = async (values, onExit) => {
  const chatConfig = {
    ...pureCloudConfig,
    logLevel: 'DEBUG',
    queueName: values.liveChatTrigger.queueName,
    locale: '',
    reconnectEnabled: true,
    data: {
      firstName: values.firstName,
      lastName: values.lastName,
      addressStreet: values.shippingAddress,
      customField1Label: 'ChatFields',
      customField1: values.liveChatTrigger.customField1,
      queueName: values.liveChatTrigger.queueName,
      customField2Label: 'customField2',
      customField2: values.customerId ?? 'null',
      customField3Label: 'customField3',
      customField3: values.email,
      customField4Label: 'customField4',
      customField4: values.reasoning,
    },
    css: {
      width: '100%',
      height: '100%',
    },
  };
  const webchat = await window.ININ.webchat.create(chatConfig);
  webchat.chatEnded = () => {
    onExit();
  };

  webchat.render({
    containerEl: CONTAINER_ID,
  });
};

const LiveChatLayout = () => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const userInfo = useLiveChat();
  const { customerId } = useMembership();
  const { tld } = useDomain();

  const onReady = () => {
    setIsScriptLoaded(true);
  };

  useEffect(() => {
    window.PURECLOUD_WEBCHAT_FRAME_CONFIG = {
      containerEl: CONTAINER_ID,
    };
    const countryCodeHandlerLiveChat = (data) => {
      const countryCodes = config.get('public.intl.defaultCountryCodes');
      const countryCode = countryCodes[tld];
      return data.replace(/{countryCode}/, countryCode);
    };
    const liveChatTrigger = {
      customField1: countryCodeHandlerLiveChat(
        config.get('public.brand.chat.triggerCustomField1')
      ),
      queueName: countryCodeHandlerLiveChat(
        config.get('public.brand.chat.queueName')
      ),
    };
    const params = requiredKeys.every((key) => {
      return Boolean(userInfo[key]);
    });

    if (!params) {
      return setIsOpen(true);
    }

    if (!isScriptLoaded) {
      return;
    }

    launchChatWidget({ ...userInfo, customerId, liveChatTrigger }, () =>
      router.push('/')
    );
  }, [customerId, isScriptLoaded, router, tld, userInfo, isOpen]);

  return (
    <>
      <Container id={CONTAINER_ID} />
      <LiveChatSurvey
        onSubmit={() => setIsOpen(false)}
        isOpen={isOpen}
        onClose={() => router.push('/')}
        defaultValues={userInfo}
      />
      <Script
        src={pureCloudScript.src}
        id={pureCloudScript.id}
        region={pureCloudScript.region}
        onReady={onReady}
        org-guid={pureCloudScript.orgGuid}
        deployment-key={pureCloudScript.deploymentKey}
        async
        globalScriptKey="purecloud"
        skipServerRender
      />
    </>
  );
};

export default LiveChatLayout;
